import { AoInstance } from "../../ao/ao"
import { getHBBaseUrl } from "../../ao/hb-router"
import { LiquidityTable, PoolLiquidity } from "../../ao/monitor-api"
import { PoolMap } from "../../ao/router"
import { TokenInfo } from "../../ao/token"
import { TokenDetails, TokenDetailsR } from "../../ao/tokendrop"
import ENV from "../../env"
import { PoolByTokensResult, PoolInfo, PoolInfoApiResponse } from "../../types/data-source"
import { createLogger } from "../../utils/logger"

const logger = createLogger("HB-DATA-SOURCE")

export class HBDataSource {
  constructor(private ao: AoInstance) {}

  async getReserves(pool: string, pair: [string, string]): Promise<[bigint, bigint, string]> {
    logger.log("Fetching reserves from HB API")

    // First check if a cron job already exists for this process
    const cronPath = `/${pool}~process@1.0/now`

    // Get current slot
    const currentSlotUrl = `${ENV.VITE_HB_API}/${pool}~process@1.0/slot/current`
    let currentSlot: number

    try {
      const currentSlotRes = await fetch(currentSlotUrl, {
        method: "GET",
      })

      if (!currentSlotRes.ok) {
        throw new Error(`Failed to fetch current slot: ${currentSlotRes.statusText}`)
      }

      currentSlot = await currentSlotRes.json()
      logger.log(`Current slot for ${pool}: ${currentSlot}`)
    } catch (error) {
      logger.error("Failed to fetch current slot", error)
      throw new Error(`Failed to fetch current slot for ${pool}`)
    }

    // Get computed slot
    const computedSlotUrl = `${ENV.VITE_HB_API}/${pool}~process@1.0/compute/at-slot`
    let computedSlot: number

    try {
      const computedSlotRes = await fetch(computedSlotUrl, {
        method: "GET",
      })

      if (!computedSlotRes.ok) {
        throw new Error(`Failed to fetch computed slot: ${computedSlotRes.statusText}`)
      }

      computedSlot = await computedSlotRes.json()
      logger.log(`Computed slot for ${pool}: ${computedSlot}`)
    } catch (error) {
      logger.error("Failed to fetch computed slot", error)
      throw new Error(`Failed to fetch computed slot for ${pool}`)
    }

    // Check slot difference
    const slotDifference = Math.abs(currentSlot - computedSlot)
    logger.log(`Slot difference for ${pool}: ${slotDifference}`)

    if (slotDifference > 100) {
      // Set once cron to sync
      const setOnceCronUrl = `${ENV.VITE_HB_API}/~cron@1.0/once?cron-path=${cronPath}`

      try {
        const cronRes = await fetch(setOnceCronUrl, {
          method: "GET",
          headers: {
            "X-API-KEY": "hypersecretkey",
          },
        })

        if (!cronRes.ok) {
          logger.error(`Failed to set once cron: ${cronRes.statusText}`)
        } else {
          logger.log(`Set once cron for ${pool} to sync slots`)
        }
      } catch (error) {
        logger.error("Failed to set once cron", error)
      }

      throw new Error(
        `Slot difference ${slotDifference} > 100 for ${pool}, triggered sync and falling back to dryrun`,
      )
    }

    // If slot difference is acceptable, proceed with current implementation
    const url = `${ENV.VITE_HB_API}/${pool}~process@1.0/compute/reserves`

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    try {
      const res = await fetch(url, {
        method: "GET",
        signal: controller.signal,
      })
      clearTimeout(timeoutId)

      if (!res.ok) {
        throw new Error(`Error fetching reserves from HB API: ${res.statusText}`)
      }

      const data = await res.json()
      const aReserves = data[pair[0]]
      const bReserves = data[pair[1]]

      if (!aReserves || !bReserves) {
        throw new Error(`Invalid reserves data from HB API: ${JSON.stringify(data)}`)
      }

      return [BigInt(aReserves), BigInt(bReserves), data["K"]]
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Request timed out after 5 seconds")
      }
      throw error
    }
  }

  async getPoolByTokens(
    pair: [string, string],
    feePercentageBps?: bigint,
  ): Promise<PoolByTokensResult | null> {
    logger.log(`Fetching pool by tokens ${pair} from HB API`)
    const response = await fetch(
      `${ENV.VITE_HB_API}/${ENV.VITE_AMM_FACTORY_CACHE_PROCESS}~process@1.0/compute/pools_by_tokens/?require-codec=application/json&accept-bundle=true`,
      {
        method: "GET",
      },
    )

    if (!response.ok) {
      throw new Error(`Error fetching pool by tokens: ${response.statusText}`)
    }

    interface PoolsByTokensResponse {
      body: {
        [key: string]: {
          [feePercentageBps: string]: string
        }
      }
    }

    const dataResponse = (await response.json()) as PoolsByTokensResponse

    const data = dataResponse.body
    if (!data) {
      throw new Error("No pool data found")
    }

    const poolKey = `${pair[0]}:${pair[1]}`
    const feeKey = feePercentageBps?.toString() || "25"

    if (data[poolKey]?.[feeKey]) {
      return {
        poolId: data[poolKey][feeKey],
        pending: false,
      }
    }

    const reversePoolKey = `${pair[1]}:${pair[0]}`
    if (data[reversePoolKey]?.[feeKey]) {
      return {
        poolId: data[reversePoolKey][feeKey],
        pending: false,
      }
    }

    // Pool doesn't exist - this is a valid state for pool creation
    logger.log(`No pool found for tokens ${pair[0]} and ${pair[1]} with fee ${feePercentageBps}`)
    return null
  }

  async getPoolMap(): Promise<PoolMap> {
    const response = await fetch(
      `${ENV.VITE_HB_API}/${ENV.VITE_AMM_FACTORY_CACHE_PROCESS}~process@1.0/compute/pools/?require-codec=application/json&accept-bundle=true`,

      {
        method: "GET",
      },
    )

    if (!response.ok) {
      throw new Error(`Error fetching pool map from HB API: ${response.statusText}`)
    }

    const data = (await response.json()) as { body: PoolMap }

    if (!data.body) {
      throw new Error("No pool map data found")
    }

    return data.body
  }

  async getTokenDetails(tokenProcess: string): Promise<TokenDetails> {
    logger.log(`Fetching token details for ${tokenProcess} from HB API`)

    const baseUrl = getHBBaseUrl(tokenProcess)

    const url = `${baseUrl}/${ENV.VITE_TOKENDROP_CACHE_PROCESS}~process@1.0/compute/tokens/${tokenProcess}`

    const res = await fetch(url, {
      method: "GET",
    })

    if (!res.ok) {
      throw new Error(`Error fetching info from HB API: ${res.statusText}`)
    }

    const tokenDetails = (await res.json()) as TokenDetailsR

    if (!tokenDetails) {
      throw new Error(`Token process ${tokenProcess} not found`)
    }

    return {
      ...tokenDetails,
      RenounceOwnership:
        typeof tokenDetails.RenounceOwnership === "string"
          ? tokenDetails.RenounceOwnership === "true"
          : tokenDetails.RenounceOwnership,
    }
  }

  async getGlobalLiquidity(): Promise<PoolLiquidity[]> {
    logger.log(`Fetching Global Liquidity from HB API`)
    const url = `${ENV.VITE_HB_API}/${ENV.VITE_GL_PROCESS}~process@1.0/compute/liquidity_table`

    const res = await fetch(url, {
      method: "GET",
    })

    if (!res.ok) {
      throw new Error(`Error fetching info from HB API: ${res.statusText}`)
    }

    const liquidityTable = (await res.json()) as LiquidityTable

    const pools: PoolLiquidity[] = Object.entries(liquidityTable).map(([key, pool]) => {
      return {
        amm_base_token: key,
        amm_token0: pool.Token_0,
        amm_quote_token: pool.Token_1,
        amm_name: key,
        amm_process: key,
        reserves_0: pool.Reserves_0,
        reserves_1: pool.Reserves_1,
        pool_fee_bps: 0, // Assuming fee is not provided in the response
        amm_status: "public", // Assuming status is public
        amm_discovered_at_ts: pool.Updated_At,
        amm_token1: pool.Token_1,
        token_0_decimals: parseInt(pool.Token_0_Denominator, 10),
        token_1_decimals: parseInt(pool.Token_1_Denominator, 10),
      }
    })

    return pools
  }

  async getTokensFromAMMFactory(): Promise<string[]> {
    logger.log(`Fetching tokens from AMM factory`)
    const url = `${ENV.VITE_HB_API}/${ENV.VITE_AMM_FACTORY_CACHE_PROCESS}~process@1.0/compute/tokens`

    const res = await fetch(url, {
      method: "GET",
    })

    if (!res.ok) {
      throw new Error(`Error fetching tokens from AMM factory: ${res.statusText}`)
    }

    const data = (await res.json()) as string[]
    return data || []
  }

  async getTokenInfo(tokenId: string): Promise<TokenInfo> {
    logger.log(`Fetching info for ${tokenId} from HB API`)

    const hburl = getHBBaseUrl(tokenId)
    const url = `${hburl}/${tokenId}~process@1.0/compute/token-info/serialize~json@1.0`

    const res = await fetch(url)

    if (!res.ok) {
      throw new Error(`Error fetching pool info from HB API: ${res.statusText}`)
    }

    const data = (await res.json()) as {
      denomination: string
      ticker: string
      logo: string
      name: string
      totalSupply: string
    }

    if (!data) {
      throw new Error(`info for ${tokenId} not found`)
    }

    return {
      id: tokenId,
      denomination: parseInt(data.denomination, 10),
      ticker: data.ticker,
      logo: data.logo,
      name: data.name,
      totalSupply: data.totalSupply ? data.totalSupply : "0",
    }
  }

  async getPoolInfo(poolId: string): Promise<PoolInfo> {
    logger.log(`Fetching pool info for ${poolId} from HB API`)
    const url = `${ENV.VITE_HB_API}/${poolId}~process@1.0/compute/token-info/serialize~json@1.0`

    const res = await fetch(url, {
      method: "GET",
    })

    if (!res.ok) {
      throw new Error(`Error fetching pool info from HB API: ${res.statusText}`)
    }

    const data = (await res.json()) as PoolInfoApiResponse

    if (!data) {
      throw new Error(`Pool info for ${poolId} not found`)
    }

    return {
      id: poolId,
      denomination: parseInt(data.Denomination, 10),
      ticker: data.Ticker,
      logo: data.Logo,
      name: data.Name,
      tokenA: data.TokenA,
      tokenB: data.TokenB,
      totalSupply: data.TotalSupply ? BigInt(data.TotalSupply) : BigInt(0),
      type: data.Type,
      fee: data.FeeBps ? BigInt(Number.parseInt(data.FeeBps)) : BigInt(0),
    }
  }

  async getBalance(tokenId: string, user: string): Promise<bigint> {
    logger.log(`Fetching balance for token ${tokenId} and user ${user} from HB API`)

    const baseUrl = getHBBaseUrl(tokenId)

    const url = `${baseUrl}/${tokenId}~process@1.0/compute/balances/${user}`

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 2000)

    try {
      const res = await fetch(url, {
        method: "GET",
      })
      clearTimeout(timeoutId)

      console.log(res.status)

      if (res.status === 404) {
        return BigInt(0)
      }

      if (!res.ok) {
        throw new Error(`Error fetching balance from HB API: ${res.statusText}`)
      }

      const data = (await res.text()) as string
      return BigInt(data || 0)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Request timed out after 2 seconds")
      }
      throw error
    }
  }

  async setCronForProcess(processId: string, every: string): Promise<string> {
    logger.log(`Setting cron for process ${processId} in HB API`)
    const url = `${ENV.VITE_HB_API}/~cron@1.0/every?cron-path=/${processId}~process@1.0/now&every=${every}`

    const res = await fetch(url, {
      method: "GET",
    })

    if (!res.ok) {
      throw new Error(`Error setting cron for process ${processId}: ${res.statusText}`)
    }

    const data = await res.json()
    return data.message || "Cron set successfully"
  }

  async getApprovedTokens(): Promise<string[]> {
    logger.log(`Fetching approved tokens from HB API`)
    const url = `${ENV.VITE_HB_API}/${ENV.VITE_DEXI_PROCESS}~process@1.0/compute/cache/community_approved_tokens`

    const res = await fetch(url, {
      method: "GET",
    })

    if (!res.ok) {
      throw new Error(`Error fetching approved tokens from HB API: ${res.statusText}`)
    }

    const data = (await res.json()) as string[]
    return data || []
  }
}
